import { doc, getDoc, Timestamp, updateDoc } from 'firebase/firestore';
import type { PurchaseRequisition } from '../types/purchasing';
import type { RfqProject } from '../types';
import { getUserInitials } from './avatarUtils';
import { db } from './firebase';
import {
  buildIssuedPurchasePayableInput,
  shouldAwardLinkedOtherQuote,
  shouldCreatePayableForIssuedPurchase,
} from './purchaseOrderIssue';
import {
  createSupplierBill,
  findSupplierBillForPurchaseOrder,
  mapPaymentTerms,
  updateSupplierBillDetails,
  type CreateSupplierBillInput,
} from './supplierBillsService';
import {
  buildOtherQuoteApprovalUpdate,
  PRE_PORTAL_RELEASE_ID,
  sanitizeRfqProjectFirestoreUpdates,
} from './timberRfqPrePortalVersion';

export async function awardLinkedOtherQuote(args: {
  requisition: PurchaseRequisition;
  actorName: string;
}): Promise<void> {
  const rfqProjectId = args.requisition.rfqProjectId?.trim();
  const rfqSubmissionId = args.requisition.rfqSubmissionId?.trim();
  if (!rfqProjectId || !rfqSubmissionId) return;

  const snap = await getDoc(doc(db, 'rfqProjects', rfqProjectId));
  if (!snap.exists()) return;
  const data = snap.data() as RfqProject;
  const update = sanitizeRfqProjectFirestoreUpdates(
    buildOtherQuoteApprovalUpdate(
      { id: snap.id, ...data },
      PRE_PORTAL_RELEASE_ID,
      rfqSubmissionId,
      true,
      getUserInitials(args.actorName),
    ),
  );
  const approvedAt = update.approvedSubmissionApprovedAt;
  const serializedAt: Record<string, unknown> = {};
  if (approvedAt) {
    for (const [key, value] of Object.entries(approvedAt)) {
      serializedAt[key] = value instanceof Date ? Timestamp.fromDate(value) : value;
    }
  }
  await updateDoc(doc(db, 'rfqProjects', rfqProjectId), {
    ...update,
    ...(approvedAt ? { approvedSubmissionApprovedAt: serializedAt } : {}),
    status: data.status === 'closed' ? data.status : 'quoted',
    updatedAt: Timestamp.now(),
  });
}

async function stampIssuedPoOnLinkedQuote(rfqSubmissionId: string | undefined, poNumber: string): Promise<void> {
  const submissionId = rfqSubmissionId?.trim();
  if (!submissionId || !poNumber.trim()) return;
  await updateDoc(doc(db, 'rfqSubmissions', submissionId), {
    poNumber,
    updatedAt: Timestamp.now(),
  });
}

export async function recordAchPayableForIssuedPurchase(args: {
  requisition: PurchaseRequisition;
  poNumber: string;
  paymentMethod?: string;
  paymentMethodId?: string;
  actorId: string;
  actorName: string;
}): Promise<void> {
  const method = {
    id: args.paymentMethodId || args.requisition.paymentMethodId,
    label: args.paymentMethod || args.requisition.paymentMethod,
  };
  if (!shouldCreatePayableForIssuedPurchase(method)) return;
  // Designer invoice card / estimating-precon POs stamp createSupplierBill=false.
  if (args.requisition.createSupplierBill === false) return;

  await stampIssuedPoOnLinkedQuote(args.requisition.rfqSubmissionId, args.poNumber);
  const existing = await findSupplierBillForPurchaseOrder(args.requisition.id);
  if (existing?.id) {
    const patch: {
      updatedAt: ReturnType<typeof Timestamp.now>;
      poNumber?: string;
    } = { updatedAt: Timestamp.now() };
    let needsPatch = false;
    if (args.poNumber && existing.poNumber !== args.poNumber) {
      patch.poNumber = args.poNumber;
      needsPatch = true;
    }
    if (needsPatch) {
      await updateDoc(doc(db, 'supplierBills', existing.id), patch);
    }
    return;
  }

  let supplierOrgId: string | undefined;
  let paymentTerms: CreateSupplierBillInput['paymentTerms'];
  if (args.requisition.rfqSubmissionId) {
    const subSnap = await getDoc(doc(db, 'rfqSubmissions', args.requisition.rfqSubmissionId));
    if (subSnap.exists()) {
      const sub = subSnap.data();
      supplierOrgId = typeof sub.supplierOrgId === 'string' ? sub.supplierOrgId : undefined;
      paymentTerms = mapPaymentTerms(sub.paymentTermsDraft);
    }
  }

  const input = buildIssuedPurchasePayableInput({
    ...args,
    supplierOrgId,
    paymentTerms,
  });
  if (!input) return;
  await createSupplierBill({
    ...input,
    ...(args.requisition.designerInvoiceId
      ? {
          qbBillStatus: args.requisition.qbBillStatus || 'pending',
          qbBillMessage:
            args.requisition.qbBillMessage
            || 'Trying to create the vendor bill in QuickBooks.',
        }
      : {}),
  });
}

/** Keep an unpaid ACH bill in sync when an issued PO amount changes within tolerance. */
export async function syncIssuedPurchasePayableAmount(args: {
  requisitionId: string;
  amount: number;
  description?: string;
}): Promise<void> {
  const existing = await findSupplierBillForPurchaseOrder(args.requisitionId);
  if (!existing?.id || existing.status === 'paid') return;
  await updateSupplierBillDetails({
    billId: existing.id,
    description: args.description,
    amount: args.amount,
  });
}

/** Award the linked Other Quote and create an ACH payable after the PO number is known. */
export async function afterPurchaseOrderIssued(args: {
  requisition: PurchaseRequisition;
  poNumber: string;
  paymentMethod?: string;
  paymentMethodId?: string;
  actorId: string;
  actorName: string;
}): Promise<void> {
  if (shouldAwardLinkedOtherQuote(args.requisition)) {
    await awardLinkedOtherQuote(args);
  }
  try {
    await recordAchPayableForIssuedPurchase(args);
  } catch (err) {
    console.warn('[purchaseOrderIssue] failed to record ACH payable', err);
  }
}
